import { apiClient } from '@/core/apiClient';
import { publicClient } from '@/core/publicClient';
import { User } from '@/core/auth';

export const authApi = {
  login: async (credentials: { email: string; password: string }): Promise<string> => {
    const response = await apiClient.post('/auth/login', credentials);
    // Handle both { token } and { data: { token } } formats
    const body = response.data;
    const token = body?.token ?? body?.data?.token;
    if (!token) throw new Error('Missing token in login response');
    return token as string;
  },

  me: async (): Promise<User> => {
    const response = await apiClient.get('/auth/me');
    const body = response.data;
    // Accept any of these shapes:
    // 1) { user: {...} }
    // 2) { data: {...} }
    // 3) { id, email, ... } (user object at top-level)
    const maybe: any = body?.user ?? body?.data ?? body;
    if (!maybe || typeof maybe !== 'object') {
      throw new Error('Missing user in /auth/me response');
    }
    // Normalize into our internal User shape (snake_case) regardless of backend casing
    const normalized: User = {
      id: Number(maybe.id),
      full_name: (maybe.full_name ?? maybe.fullName ?? '') as string,
      email: (maybe.email ?? '') as string,
      // Prefer numeric role_id; do not fall back to string role here
      role_id: (maybe.role_id ?? maybe.roleId ?? null) as number,
      // Prefer string role name; if only a string "role" is provided, use that
      role_name: (maybe.role_name ??
        maybe.roleName ??
        (typeof maybe.role === 'string' ? maybe.role : undefined) ??
        undefined) as any,
      birth_date: (maybe.birth_date ?? maybe.birthDate ?? undefined) as string | undefined,
      phone: (maybe.phone ?? undefined) as string | undefined,
      is_active: (maybe.is_active ?? maybe.isActive ?? true) as boolean,
      shelter: maybe.shelter
        ? {
            shelterId: Number(maybe.shelter.shelterId ?? maybe.shelter.shelter_id ?? maybe.shelter.id),
            shelterName: (maybe.shelter.shelterName ?? maybe.shelter.shelter_name ?? maybe.shelter.name ?? undefined) as any,
            roleInShelter: (maybe.shelter.roleInShelter ?? maybe.shelter.role_in_shelter ?? undefined) as any,
          }
        : undefined,
    };
    return normalized;
  },

  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Ignore server errors on logout to ensure client state clears
    }
  },

  registerCustomer: async (payload: {
    fullName: string;
    email: string;
    phone: string;
    birthDate?: string;
    password: string;
  }) => {
    const body = {
      ...payload,
      password: payload.password,
    };
    const response = await publicClient.post('/public/register', body);
    return response.data as {
      success?: boolean;
      token?: string;
      user?: any;
    };
  },

  checkEmailExists: async (email: string): Promise<boolean> => {
    const res = await publicClient.get('/public/check-email', { params: { email } });
    return Boolean(res.data?.exists);
  },

  firebaseLogin: async (
    idToken: string,
    profile?: { fullName?: string; phone?: string; birthDate?: string }
  ): Promise<string> => {
    const response = await publicClient.post('/auth/firebase-login', { idToken, ...profile });
    const body = response.data;
    const token = body?.token ?? body?.data?.token;
    if (!token) throw new Error('Missing token in firebase-login response');
    return token as string;
  },

  autoLoginByEmail: async (payload: { email: string }) => {
    const response = await publicClient.post('/public/webapp/auto-login', payload);
    return response.data as {
      success?: boolean;
      exists?: boolean;
      loggedIn?: boolean;
      reason?: string;
      token?: string;
      user?: any;
    };
  },
};
